import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ActivityType, RepoHoursBreakdown } from "@torre/work-hours";
import type { ThemeClasses } from "../../../types/components";
import { repoUrl } from "../lib/github";
import { byPerson, countedLines, groupLines, hoursOf, sumCost, type Ledger } from "../lib/ledger";
import { CountedLinesTable, ExpandRow, ForeignPart, MoneyText, PeopleSubtotals } from "./LedgerParts";

// ---------------------------------------------------------------------------
// «Por repositorio» (#1342 / #1344).
//
// El total del tablero suma lo que CUENTA (horas registradas; estimado en los
// días sin registro). La actividad por commits es otra medida: el tiempo que
// demuestran los commits. Cada repo trae las dos y, para los dueños, lo que
// cuestan sus horas (las de cada persona × su costo por hora).
//
// Cada repo se abre: el subtotal por colaborador y las líneas que suman sus
// horas —entradas registradas a mano, entradas con commits, reuniones del CRM o
// el estimado de un día sin registro—, con quién las registró y su costo.
// ---------------------------------------------------------------------------

export { hoursOf };

interface Props {
  breakdown: RepoHoursBreakdown;
  /** Las líneas detrás de cada repo; `null` mientras cargan (los renglones no se abren). */
  ledger: Ledger | null;
  catalog: readonly ActivityType[] | null;
  tc: ThemeClasses;
  isDarkMode: boolean;
  /** Dentro de una pestaña que ya lleva el título. */
  hideTitle?: boolean;
}

const NONE = "__none";

export default function RepoBreakdown({ breakdown: b, ledger, catalog, tc, isDarkMode, hideTitle = false }: Props) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState<string | null>(null);
  const groups = useMemo(() => new Map((ledger ? groupLines(countedLines(ledger).filter((l) => !l.otherRepo), (l) => l.repo) : []).map((g) => [g.key, g])), [ledger]);
  const costs = Boolean(ledger?.costs);
  const max = Math.max(1, ...b.byRepo.map((r) => Math.max(r.countedMinutes, r.activityMinutes)));
  const track = isDarkMode ? "bg-gray-700" : "bg-gray-100";

  return (
    <div data-testid="repo-breakdown">
      {!hideTitle && <p className={`mb-1 text-sm font-semibold ${tc.text}`}>{t("workHours.repos.title")}</p>}
      <p className={`mb-2 text-xs ${tc.textSecondary}`}>{t("workHours.repos.subtitle")}</p>
      {b.byRepo.map((r) => {
        const id = r.repo || NONE;
        const group = groups.get(r.repo);
        const url = r.repo ? repoUrl(r.repo) : null;
        const caption =
          r.differenceMinutes === 0
            ? null
            : r.countedMinutes === 0
              ? t("workHours.repos.rowOnlyCommits")
              : r.differenceMinutes < 0
                ? t("workHours.repos.rowLess", { hours: hoursOf(-r.differenceMinutes) })
                : r.withoutCommitsMinutes > 0
                  ? t("workHours.repos.rowWithoutCommits", { hours: hoursOf(r.withoutCommitsMinutes) })
                  : (r.supportMinutes ?? 0) > 0
                    ? t("workHours.repos.rowSupport", { hours: hoursOf(r.supportMinutes) })
                    : t("workHours.repos.rowBeyond", { hours: hoursOf(r.differenceMinutes) });
        return (
          <ExpandRow
            key={id}
            tc={tc}
            testId={`repo-breakdown-row-${r.repo || "none"}`}
            open={open === id}
            onToggle={() => setOpen(open === id ? null : id)}
            disabled={!group}
            label={
              <span className="block">
                <span className="break-all">{r.repo || t("workHours.repos.noRepo")}</span>
                <span className={`mt-1 block h-1.5 w-40 max-w-full rounded-full ${track}`}>
                  <span className="block h-1.5 rounded-full bg-violet-500" style={{ width: `${Math.min(100, (r.countedMinutes / max) * 100)}%` }} />
                </span>
                <span className={`mt-0.5 block h-1 w-40 max-w-full rounded-full ${track}`}>
                  <span className="block h-1 rounded-full bg-sky-500" style={{ width: `${Math.min(100, (r.activityMinutes / max) * 100)}%` }} />
                </span>
                {caption && <span className={`mt-0.5 block text-[11px] ${tc.textSecondary}`}>{caption}</span>}
              </span>
            }
            right={
              <>
                <span className="font-semibold">{t("workHours.repos.counted", { hours: hoursOf(r.countedMinutes) })}</span>
                <span className={`block text-xs ${tc.textSecondary}`}>{t("workHours.repos.byCommits", { hours: hoursOf(r.activityMinutes) })}</span>
                {costs && <MoneyText money={group ? group.money : sumCost([])} className={`block text-xs ${tc.textSecondary}`} />}
                {costs && group && <ForeignPart money={group.money} className={tc.textSecondary} />}
              </>
            }
            footer={
              // Fuera del botón (un enlace no puede ir dentro) y siempre visible:
              // también mientras carga el detalle y en repos que solo tienen commits.
              url ? (
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-violet-500 hover:underline">
                  {t("workHours.repositories.openOnGithub", { repo: r.repo })}
                </a>
              ) : undefined
            }
          >
            {group && (
              <>
                <PeopleSubtotals people={byPerson(group.lines)} costs={costs} tc={tc} />
                <CountedLinesTable lines={group.lines} costs={costs} catalog={catalog} tc={tc} hideRepo testId={`repo-lines-${r.repo || "none"}`} />
              </>
            )}
          </ExpandRow>
        );
      })}
      <p className={`mt-2 text-[11px] ${tc.textSecondary}`}>
        <span className="mr-1 inline-block h-1.5 w-3 rounded-full bg-violet-500 align-middle" />
        {t("workHours.repos.legendCounted")}
        <span className="ml-3 mr-1 inline-block h-1.5 w-3 rounded-full bg-sky-500 align-middle" />
        {t("workHours.repos.legendCommits")}
      </p>
    </div>
  );
}
